/**
 * Alert Service
 * @description Service for managing alerts
 */

import api from './api';
import type { AlertsResponse, AlertFilters, AlertDetail, AlertActivityLogsResponse } from '@/types/alert';

/**
 * Get all alerts with optional filters
 * @param filters - Optional filters: alert_id, site_id, site_url, origin, severity, created_at, start_date, end_date, search, include_archived, limit, offset
 * @returns Promise with alerts response containing total count and alerts array
 */
export const getAlerts = async (filters?: AlertFilters): Promise<AlertsResponse> => {
  // Build query parameters
  const params: Record<string, string | number | boolean> = {};
  
  if (filters?.alert_id) {
    params.alert_id = filters.alert_id;
  }
  if (filters?.site_id !== undefined && filters.site_id !== null) {
    params.site_id = filters.site_id;
  }
  if (filters?.site_url) {
    params.site_url = filters.site_url;
  }
  if (filters?.origin) {
    params.origin = filters.origin;
  }
  if (filters?.severity) {
    params.severity = filters.severity;
  }
  if (filters?.status_code) {
    params.status_code = filters.status_code;
  }
  if (filters?.created_at) {
    params.created_at = filters.created_at;
  }
  if (filters?.start_date) {
    params.start_date = filters.start_date;
  }
  if (filters?.end_date) {
    params.end_date = filters.end_date;
  }
  if (filters?.search) {
    params.search = filters.search;
  }
  if (filters?.include_archived !== undefined) {
    params.include_archived = filters.include_archived;
  }
  if (filters?.limit !== undefined) {
    params.limit = filters.limit;
  } else {
    params.limit = 100; // Default limit
  }
  if (filters?.offset !== undefined) {
    params.offset = filters.offset;
  } else {
    params.offset = 0; // Default offset
  }

  return await api.get<AlertsResponse>('/alerts', params);
};

/**
 * Get alert by ID
 * @param id - Alert ID (e.g., 'ALR20251126143542001')
 * @returns Promise with alert detail
 */
export const getAlertById = async (id: string): Promise<AlertDetail> => {
  try {
    return await api.get<AlertDetail>(`/alerts/${id}`);
  } catch (error) {
    console.error('Error fetching alert:', error);
    throw error;
  }
};

/**
 * Get alerts for a specific site
 * @param siteId - Site ID
 * @param filters - Optional filters: severity, start_date, end_date, limit, offset
 * @returns Promise with alerts response
 */
export const getSiteAlerts = async (
  siteId: string | number,
  filters?: Omit<AlertFilters, 'site_id'>
): Promise<AlertsResponse> => {
  return getAlerts({ ...filters, site_id: Number(siteId) });
};

/**
 * Get alert activity logs
 * @param id - Alert ID
 * @returns Promise with activity logs response
 */
export const getAlertActivities = async (id: string): Promise<AlertActivityLogsResponse> => {
  try {
    return await api.get<AlertActivityLogsResponse>(`/alerts/${id}/activity`);
  } catch (error) {
    console.error('Error fetching alert activities:', error);
    throw error;
  }
};

/**
 * Raise incident from alert(s)
 * @param alertIds - Array of alert IDs to raise incidents for
 * @returns Promise with success status and optional incident_id
 */
export const raiseIncident = async (alertIds: string[]): Promise<{ success: boolean; incident_id?: string }> => {
  try {
    return await api.post<{ success: boolean; incident_id?: string }>('/alerts/raise-incident', {
      alert_ids: alertIds,
    });
  } catch (error) {
    console.error('Error raising incident:', error);
    throw error;
  }
};

/**
 * Raise incident from a single alert
 * @param alertId - Alert ID to raise incident for
 * @returns Promise with success status and optional incident_id
 */
export const raiseIncidentFromAlert = async (alertId: string): Promise<{ success: boolean; incident_id?: string }> => {
  try {
    return await api.post<{ success: boolean; incident_id?: string }>(`/incidents/raise/${alertId}`);
  } catch (error) {
    console.error('Error raising incident from alert:', error);
    throw error;
  }
};

const alertService = {
  getAlerts,
  getAlertById,
  getSiteAlerts,
  getAlertActivities,
  raiseIncident,
  raiseIncidentFromAlert,
};

export default alertService;

